// Management authority level, carried by a role (not by the member directly) — as
// returned/accepted by the REST surface (lowercase — hand-rolled JSON, not a GraphQL enum).
// 'owner' is never settable via the roles API: there is exactly one seeded Owner role per org.
export type ManagementLevel = 'owner' | 'admin' | 'member';

// -- Roles --------------------------------------------------------

export interface OrgRoleCapability {
  key: string;
  label: string;
}

export interface OrgRole {
  id: string;
  name: string;
  is_default: boolean;
  management_level: ManagementLevel;
  capabilities: OrgRoleCapability[];
}

export interface OrgRoleListResponse {
  roles: OrgRole[];
}

export interface OrgRoleResponse {
  role: OrgRole;
}

export interface CreateOrgRoleInput {
  name: string;
  // Only 'admin'/'member' are settable via the API; omit to default to 'member'.
  management_level?: 'admin' | 'member';
  capability_keys: string[];
}

export interface UpdateOrgRoleInput {
  name?: string;
  // Only 'admin'/'member' are settable via the API; omit to leave unchanged.
  management_level?: 'admin' | 'member';
  capability_keys?: string[];
}

// -- Service catalog --------------------------------------------------------

export interface OrgServiceCatalogItem {
  key: string;
  label: string;
}

export interface OrgServiceCatalogResponse {
  modules: OrgServiceCatalogItem[];
}

// -- Members ------------------------------------------------------------

export interface OrgMember {
  id: string;
  user_id: string;
  email: string;
  // Derived from the assigned role (role_id); null only if no role is assigned.
  management_level: ManagementLevel | null;
  status: string;
  role_id: string | null;
  role_name: string | null;
}

export interface OrgMemberListResponse {
  members: OrgMember[];
}

/*
 * `OrgMemberResponse` and `AssignMemberRoleInput` were the shapes of
 * `PATCH /orgs/:organizationId/members/:memberId`, which the backend has retired in favour of the
 * `updateOrgUserRole` GraphQL mutation. Removed rather than left in place: a type for a dead endpoint
 * is an invitation to call it.
 */

// -- Invitations --------------------------------------------------------

export type OrgInvitationStatus = 'pending' | 'accepted' | 'cancelled' | 'expired';

export interface OrgInvitation {
  id: string;
  email: string;
  role_id: string | null;
  status: OrgInvitationStatus;
  expires_at: string;
}

export interface OrgInvitationListResponse {
  invitations: OrgInvitation[];
}

export interface OrgInvitationResponse {
  invitation: OrgInvitation;
}

export interface CreateOrgInvitationInput {
  email: string;
  role_id: string;
}

// -- Usage & budgets (LiteLLM-backed) --------------------------------------

// Allowlisted by the backend — `/usage/report` rejects anything else with a 422,
// because `days` widens an org-unfiltered `/spend/logs` pull.
export type UsageWindowDays = 7 | 15 | 30;

// The three calendar-month options `?month=` accepts, alternative to `?days=` above.
// `this_quarter` is a ROLLING three-calendar-month window ending today (current + previous + the
// closed month before that), not a fixed Jan-Mar/Apr-Jun/etc. fiscal quarter — matches the backend's
// `UsageContext.month_bounds/2`.
export type UsageWindowMonth = 'current' | 'previous' | 'this_quarter';

/**
 * A selected reporting range: a fixed day window, or a calendar month.
 *
 * Declared here rather than in `hooks/org-admin/useOrgUsageReport` (where it started) because the
 * pure adapters in `analysis/` and `overview/` need it, and a service-layer module importing from a
 * hook inverts the dependency — the hook re-exports it for the callers that already reference it
 * there.
 */
export type UsageWindowSelection = UsageWindowDays | UsageWindowMonth;

/** Narrows a selection to a calendar month. A day window is always a number. */
export function isMonthSelection(
  selection: UsageWindowSelection,
): selection is UsageWindowMonth {
  return typeof selection !== 'number';
}

// Shape shared by GET /usage's members[] and GET/PUT /usage/members/:user_id —
// spend/budget only, no token counts (tokens exist only in the report below).
export interface MemberUsage {
  user_id: string;
  // Populated on the org rollup; always null on the single-seat endpoints.
  email: string | null;
  spend_usd: number;
  budget_usd: number;
  remaining_usd: number;
  resets_at: string | null;
  over_budget: boolean;
  /**
   * The seat's own subscription state, on the single-seat endpoint only — a member can't read
   * `GET /billing`. Absent on the org rollup and on a backend predating the field.
   */
  billing?: OrgBillingMember | null;
  /**
   * Where `budget_usd` comes from — backend PR #327 (2026-10-05, `docs/billing-api.md` §Credits).
   *
   * `budget_usd = plan_budget_usd + credit_balance_usd` and `remaining_usd = plan_remaining_usd +
   * credit_remaining_usd`. Spend draws from the plan first, then from credits. Credits CARRY OVER
   * across cycle resets and survive a plan ending; the plan part resets to the plan amount.
   *
   * All four are optional because a backend predating the PR omits them, and an absent breakdown
   * must render as "not available" — never as "$0 of credit". Read them through
   * `services/org-admin/allowance.ts`, which is the one place that decides what absence means.
   */
  plan_budget_usd?: number;
  plan_remaining_usd?: number;
  credit_balance_usd?: number;
  credit_remaining_usd?: number;
}

export interface OrgUsageRollup {
  organization_id: string;
  total_spend_usd: number;
  member_count: number;
  // Active members only — contrast OrgUsageReport.members, which is every status.
  members: MemberUsage[];
}

export interface OrgUsageReportModelBreakdown {
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  spend_usd: number;
}

export interface OrgUsageReportMember {
  user_id: string;
  email: string | null;
  status: string;
  prompt_tokens: number;
  completion_tokens: number;
  // Window-scoped — NOT the same clock as budget_usd/resets_at below.
  spend_usd_window: number;
  // Since cycle_started_at (this member's last reset, or their org-join date
  // if never reset). This is what LiteLLM actually enforces budget_usd
  // against — pair spend_usd_cycle with budget_usd, not spend_usd_lifetime.
  prompt_tokens_cycle: number;
  completion_tokens_cycle: number;
  spend_usd_cycle: number;
  // True all-time spend — survives a cycle reset, unlike spend_usd_cycle.
  spend_usd_lifetime: number;
  cycle_started_at: string | null;
  budget_usd: number;
  resets_at: string | null;
  blocked: boolean;
  by_model: OrgUsageReportModelBreakdown[];
  /**
   * The plan / credits split of `budget_usd`, computed by the backend against THIS row's
   * `spend_usd_cycle` — i.e. on the enforced clock, not the reporting month. Same semantics and
   * same absence rule as on `MemberUsage`. NOT carried by the batched `/usage/reports` endpoint.
   */
  plan_budget_usd?: number;
  plan_remaining_usd?: number;
  credit_balance_usd?: number;
  credit_remaining_usd?: number;
}

export interface OrgUsageReportTotals {
  prompt_tokens: number;
  completion_tokens: number;
  spend_usd: number;
  spend_usd_lifetime: number;
  // Org-wide sum of each member's spend_usd_cycle/*_tokens_cycle — see the
  // per-member field comments above for what "cycle" means here.
  prompt_tokens_cycle: number;
  completion_tokens_cycle: number;
  spend_usd_cycle: number;
}

/**
 * One LiteLLM customer under this org that belongs to no member.
 *
 * `ncoder-rag-service` and `playground` mint a synthetic `"{org_id}:pipeline"` seat for batch work
 * with no live user. Reports enumerated members only, so this spend was omitted everywhere —
 * measured at 87% of all spend in dev. Discovered by elimination rather than by matching the name,
 * so a future convention is reported the day it appears.
 */
export interface OrgAutomationSeat {
  /** The customer-id suffix, e.g. `pipeline`. */
  name: string;
  customer_id: string;
  spend_usd_window: number;
  spend_usd_lifetime: number;
  prompt_tokens: number;
  completion_tokens: number;
}

/**
 * Org spend that belongs to no seat.
 *
 * Deliberately NOT part of `org_total`: that field is paired with seat budgets throughout, and
 * automation seats have no budget. Anything showing the true bill adds the two together and says
 * it is doing so.
 */
export interface OrgAutomationUsage {
  spend_usd_window: number;
  spend_usd_lifetime: number;
  prompt_tokens: number;
  completion_tokens: number;
  seats: OrgAutomationSeat[];
}

export interface OrgUsageReport {
  // Exactly one of window_days/month is non-null, mirroring the backend's `?days=`/`?month=`
  // mutual exclusivity — `month` wins when both are requested.
  window_days: UsageWindowDays | null;
  month: UsageWindowMonth | null;
  // Non-empty entries name upstream sources that are not fully reliable: "customers" and
  // "spend_logs" failed and were zeroed; "spend_logs_capped" means the fetch SUCCEEDED but LiteLLM
  // refused to count past its row ceiling, so the figures are real but low. Warn on any of them.
  partial: Array<'customers' | 'spend_logs' | 'spend_logs_capped'>;
  org_total: OrgUsageReportTotals;
  // Every member regardless of status — deactivated seats keep their spend visible.
  members: OrgUsageReportMember[];
  // Spend booked to the org that belongs to no member. Optional so an older backend degrades to
  // showing nothing rather than to claiming zero automation spend.
  automation?: OrgAutomationUsage;
}

/**
 * `PUT /usage/members/:id/budget` sets the PLAN allowance only (backend #327). The member's credits
 * are added on top server-side, so the resulting `budget_usd` is `amount_usd + credit_balance_usd`.
 * The PUT response does NOT carry the plan/credit split — re-fetch the member afterwards.
 */
export interface SetMemberBudgetInput {
  amount_usd: number;
}

// -- Usage service report (observability-backed) --------------------------
// Distinct data source from OrgUsageReport above: read from `observability_events`
// (populated by product services), not LiteLLM. No budget/blocked concept here —
// budgets remain LiteLLM-only, sourced from OrgUsageReport/OrgUsageRollup.

export interface OrgServiceUsageBreakdown {
  // Raw slug a product service sends when ingesting events — no friendly-label
  // mapping happens server-side (e.g. 'multi-step-agentic' needs a display label).
  service: string;
  event_count: number;
  run_count: number;
  failed_count: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  avg_latency_ms: number | null;
  // Cycle-scoped (this member's own budget cycle, not the shared window above) —
  // only ever present on the per-user report's by_service rows, and even there
  // may be absent if the backend's cycle lookup failed (it degrades gracefully
  // rather than blanking the response). Never present on the org-level report.
  // run_count_cycle/failed_count_cycle exist because a run is what produces
  // the tokens it's paired with — no avg_latency_ms_cycle, since latency isn't
  // a consumption metric a budget reset should affect.
  input_tokens_cycle?: number;
  output_tokens_cycle?: number;
  cost_usd_cycle?: number;
  run_count_cycle?: number;
  failed_count_cycle?: number;
}

export interface OrgServiceUsageReport {
  // Exactly one of window_days/month is non-null — same `?days=`/`?month=` contract as
  // OrgUsageReport above.
  window_days: UsageWindowDays | null;
  month: UsageWindowMonth | null;
  event_count: number;
  run_count: number;
  failed_count: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  avg_latency_ms: number | null;
  // Sorted by `service` ascending server-side; resort client-side for display order.
  by_service: OrgServiceUsageBreakdown[];
}

// Per-member version of the same observability data — every org member appears
// here, zero-filled if inactive (see `ObservabilityContext.user_summary/3`).
export interface OrgServiceUsageReportUser {
  user_id: string;
  email: string | null;
  status: string;
  event_count: number;
  run_count: number;
  failed_count: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  avg_latency_ms: number | null;
  by_service: OrgServiceUsageBreakdown[];
  // Cycle-scoped totals — see OrgServiceUsageBreakdown's cycle fields for why
  // these are optional rather than guaranteed.
  input_tokens_cycle?: number;
  output_tokens_cycle?: number;
  cost_usd_cycle?: number;
  run_count_cycle?: number;
  failed_count_cycle?: number;
}

export interface OrgServiceUsageReportByUsers {
  window_days: UsageWindowDays | null;
  month: UsageWindowMonth | null;
  users: OrgServiceUsageReportUser[];
}

// -- Member lifecycle (GraphQL only — active/deactivated) --

// GraphQL enum external representations are uppercase (Absinthe default), unlike the REST
// surface above — see e.g. services/metadata_v1/types.ts's BranchStatus for the same convention.
export type OrgAdminMembershipRoleGQL = 'OWNER' | 'ADMIN' | 'MEMBER';
export type OrgAdminStatusGQL = 'ACTIVE' | 'INVITED' | 'DEACTIVATED';

/**
 * `updateOrgUserRole`'s selection set.
 *
 * Its own type rather than fields bolted onto `OrgAdminUser`, because a GraphQL response type IS the
 * selection, not the whole schema type: the deactivate/reactivate mutations select
 * `deactivatedAt`/`deactivatedByUserId` and no role fields, this one selects `roleId`/`roleName` and
 * neither deactivation field. Merging them would mean four optional properties that are silently
 * `undefined` depending on which mutation produced the object.
 *
 * There is no `owner`/`admin`/`member` input anywhere in this flow. Authority is a PROPERTY OF THE
 * ROLE — you assign a `roleId`, and the resulting `role` is whatever that role's `management_level`
 * is. That is why `role` is returned rather than sent.
 */
export interface OrgAdminUserRoleAssignment {
  memberId: string;
  organizationId: string;
  userId: string;
  /** Derived server-side from the assigned role's management level — never supplied by the client. */
  role: OrgAdminMembershipRoleGQL;
  roleId: string;
  roleName: string;
  status: OrgAdminStatusGQL;
}

export interface OrgAdminUser {
  memberId: string;
  organizationId: string;
  userId: string;
  role: OrgAdminMembershipRoleGQL;
  status: OrgAdminStatusGQL;
  deactivatedAt: string | null;
  deactivatedByUserId: string | null;
  updatedAt: string;
}

// -- Billing (Stripe-backed) ------------------------------------------------
// docs/billing-api.md is the contract. Plan tiers are fixed server-side config
// (`UsageContext.list_plans/0`) with no endpoint to fetch them, so the catalogue
// below is mirrored here — the one place these numbers live on the client.

export type BillingPlan = 'free' | 'starter' | 'growth' | 'scale';

/** Only these three are purchasable; `free` is the default and the cancellation fallback. */
export type PurchasablePlan = Extract<BillingPlan, 'starter' | 'growth' | 'scale'>;

/**
 * The only paid mode. One-time purchases were removed 2026-09-11 — every paid seat is a
 * subscription. `null` on a seat still means something distinct: free, with no subscription at all.
 */
export type BillingMode = 'recurring';

export interface PlanTier {
  plan: PurchasablePlan;
  /** What the customer pays. */
  priceUsd: number;
  /** The LiteLLM spend ceiling the plan grants — deliberately different from priceUsd. */
  budgetUsd: number;
}

/**
 * Mirrors the server's plan table. `priceUsd` and `budgetUsd` diverge on purpose
 * (growth costs $50 and grants a $100 spend cap); the UI must never present one
 * as the other.
 */
export const BILLING_PLANS: PlanTier[] = [
  { plan: 'starter', priceUsd: 30, budgetUsd: 30 },
  { plan: 'growth', priceUsd: 50, budgetUsd: 100 },
  { plan: 'scale', priceUsd: 80, budgetUsd: 240 },
];

/**
 * The saved card's basics, as Stripe reports them.
 *
 * Deliberately the whole of what the frontend knows about the card: a brand, the last four digits
 * and an expiry. Enough to answer "which card is this?" and nothing that could be used to charge it.
 */
export interface PaymentMethodInfo {
  /** Stripe's lowercase brand slug — `visa`, `mastercard`, `amex`, …, or `unknown`. */
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface OrgBillingMember {
  user_id: string;
  /**
   * The seat's plan, read straight off the row. Authoritative — never inferred.
   *
   * Before the backend carried this, the UI matched a seat to a plan by the AI budget it held, which
   * misread every topped-up seat. That inference is gone; this field replaced it.
   */
  plan: BillingPlan;
  /** `null` once a seat has never purchased, or after a cancellation downgrades it to free. */
  billing_mode: BillingMode | null;
  /** Stripe's status string, or the literal `"none"` when there is no subscription. */
  subscription_status: string;
  /**
   * When this seat's subscription next renews.
   *
   * `null` for a free seat, or one that was never billed. It can be momentarily in the PAST: the
   * period rolls at Stripe before `customer.subscription.updated` reaches our backend, so any reader
   * has to handle a date that has already gone by.
   */
  plan_cycle_ends_at: string | null;
  /**
   * True once a cancellation is already scheduled at Stripe. `subscription_status` alone can't show
   * this — Stripe keeps reporting `"active"` for the whole remainder of the period, whether the seat
   * cancelled through this app, the Stripe Customer Portal, or the Stripe dashboard directly. `false`
   * for a backend predating the field, which reads the same as "not cancelling" and is the safe
   * default either way.
   */
  cancel_at_period_end: boolean;
  /** When the cancellation above takes effect — the same instant as `plan_cycle_ends_at`. `null` unless `cancel_at_period_end` is true. */
  cancel_at: string | null;
  /**
   * The paid plan whose subscription ended, and when. Set once Stripe ends the subscription, cleared
   * by the next purchase. While set the seat is on `free` with a $0 budget: an ended plan does not
   * fall back to Free's allowance. Optional for a backend predating the fields.
   */
  ended_plan?: BillingPlan | null;
  plan_ended_at?: string | null;
}

export interface OrgBillingStatus {
  has_payment_method: boolean;
  /**
   * Extra budget granted per dollar topped up — `1.3` means $10 buys $13 of spend allowance.
   *
   * Served by the API rather than mirrored here as a constant, unlike `BILLING_PLANS`. The plan
   * catalogue is static config that changes with a deploy; this is applied to a live card charge,
   * and a client-side copy that drifted from the server's would state a bonus the customer does
   * not receive. Optional so a backend predating the field degrades to saying nothing rather than
   * to claiming `1`.
   */
  topup_bonus_multiplier?: number;
  /**
   * Card basics when one is on file AND its details were fetched.
   *
   * `null` covers two different situations that the backend deliberately collapses into one: there is
   * no card, or there is a card whose details could not be read. In the second case
   * `has_payment_method` still reports `true`, so the two fields are read together — `has_payment_method`
   * decides whether a card exists, and this decides whether we can name it.
   */
  payment_method: PaymentMethodInfo | null;
  /** Active members only — deactivated seats are omitted even with a live subscription. */
  members: OrgBillingMember[];
}

/**
 * A tier change on a seat that ALREADY has a live subscription.
 *
 * Deliberately not `CreateCheckoutInput` with a different plan: the checkout endpoint refuses a
 * seat with an active subscription (409 `subscription_already_active`), because a second Checkout
 * would create a second live Stripe Subscription billing alongside the first. A change reprices
 * the existing subscription instead, and is prorated.
 *
 * No `billing_mode`: every paid seat is a subscription, so there is nothing to choose.
 */
export interface ChangePlanInput {
  user_id: string;
  plan: PurchasablePlan;
}

export interface ChangePlanResponse {
  plan: PurchasablePlan;
  /**
   * Which way the change went, decided server-side from the plans' prices.
   *
   * The UI must not re-derive this from the tier order: an upgrade charges the prorated difference
   * to the card now, whereas a downgrade becomes a CREDIT against the next invoice and never a
   * refund. Telling someone they have been refunded when they have not is the failure this field
   * exists to prevent.
   */
  direction: 'upgrade' | 'downgrade';
}

// -- Invoices ---------------------------------------------------------------

export interface OrgInvoiceLine {
  id: string;
  description: string | null;
  quantity: number | null;
  /** Dollars — the backend converts from Stripe's minor units once, at the client boundary. */
  amount: number | null;
  /** Present on prorated lines, which is what a plan change produces. */
  period_start: string | null;
  period_end: string | null;
}

export interface OrgInvoice {
  id: string;
  /** `null` for a draft — Stripe assigns the human-facing number only at finalisation. */
  number: string | null;
  status: string;
  currency: string;
  total: number | null;
  amount_due: number | null;
  created: string | null;
  period_start: string | null;
  period_end: string | null;
  /** Stripe-hosted, carries its own signed access — opened directly, never proxied. */
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  lines: OrgInvoiceLine[];
}

export interface OrgInvoiceListResponse {
  invoices: OrgInvoice[];
}

/** One seat's share of a bulk top-up. */
export interface TopUpAllocation {
  user_id: string;
  amount_usd: number;
}

/**
 * One payment split across several seats.
 *
 * Separate from `TopUpInput` rather than an array form of it, mirroring the API: the two have
 * different failure semantics (this validates the WHOLE allocation before charging anything, so a
 * single non-member rejects the request instead of charging for the valid subset).
 */
export interface BulkTopUpInput {
  allocations: TopUpAllocation[];
}

/** Same two shapes as a single top-up: charged off-session, or sent to Checkout. */
export type BulkTopUpResponse = { status: 'processing' } | { checkout_url: string };

// -- Org credit pool --------------------------------------------------------
// Buy once into an organization-level balance, assign to seats later. Distinct from a top-up,
// which charges and credits one named seat in a single step.

export interface OrgCreditEntry {
  id: string;
  /** `purchase` is positive, `assignment` is negative. The balance is their sum. */
  kind: 'purchase' | 'assignment';
  amount_usd: number;
  /** Purchase rows only — what the card was charged, before the bonus. */
  amount_paid_usd: number | null;
  /** Purchase rows only — the rate in force then, so a later change cannot restate this row. */
  bonus_multiplier: number | null;
  /** Assignment rows only. */
  assigned_to_email: string | null;
  inserted_at: string;
}

export interface OrgCreditsResponse {
  balance_usd: number;
  entries: OrgCreditEntry[];
}

export interface PurchaseCreditsInput {
  amount_usd: number;
}

/** Same two shapes as every other purchase path: charged off-session, or sent to Checkout. */
export type PurchaseCreditsResponse = { status: 'processing' } | { checkout_url: string };

export interface AssignCreditsInput {
  allocations: TopUpAllocation[];
}

export interface AssignCreditsResponse {
  assigned_usd: number;
  /** The ORG POOL balance left after the assignment — not any member's balance. */
  remaining_usd: number;
  /**
   * Seats whose budget update did not apply. The pool was still debited for them, so the caller
   * must warn per seat rather than report a clean success. Optional for a backend predating #327.
   */
  failed_user_ids?: string[];
}

// No `billing_mode`: Checkout is always subscription mode now.
export interface CreateCheckoutInput {
  user_id: string;
  plan: PurchasablePlan;
}

/**
 * @deprecated Backend PR #327: the card top-up endpoints (`/billing/topup`, `/billing/topup/bulk`,
 * `/usage/members/:id/topup`) are slated for removal and their amounts are still wiped at cycle
 * reset. Nothing in the panel mounts them any more; money reaches a seat via the credit pool
 * (`AssignCreditsInput`), which carries over. Kept only until the endpoints are gone server-side.
 */
export interface TopUpInput {
  user_id: string;
  amount_usd: number;
}

/** Checkout always returns a URL to redirect to; the plan lands later via the webhook. */
export interface CheckoutResponse {
  checkout_url: string;
}

/**
 * Top-up is one of two shapes: a card on file means the charge is already in flight,
 * no card means Checkout (which also saves the card). Both must be handled.
 */
export type TopUpResponse = { status: 'processing' } | { checkout_url: string };

export interface PortalResponse {
  portal_url: string;
}

/** Stripe's status at the moment of the cancel call — typically still `"active"`. */
export interface CancelSubscriptionResponse {
  status: string;
}

// -- Usage timeseries (observability + LiteLLM, bucketed by day) -------------
// The one endpoint in this namespace with a time dimension. `days` selects how
// far back to bucket, not how wide to aggregate — contrast OrgUsageReport and
// OrgServiceUsageReport above, both of which return a single window total.

export interface OrgUsageTimeseriesPoint {
  /** Calendar day (UTC), `YYYY-MM-DD`. */
  date: string;
  /**
   * LiteLLM spend for the day, bucketed from the spend logs' own `startTime`.
   *
   * On the same clock as `budget_usd` above, which is what makes it legitimate to
   * draw a budget line across a chart of this series. The observability payloads
   * also carry a `cost_usd`, and it is deliberately NOT what this is — see
   * `UsageContext.get_org_usage_timeseries/2`.
   */
  spend_usd: number;
  /** From the spend logs, so named as LiteLLM names them once summed per day. */
  input_tokens: number;
  output_tokens: number;
  /** These four come from `observability_events`, never from the spend logs. */
  run_count: number;
  event_count: number;
  failed_count: number;
  /** Distinct members with at least one event that day. */
  active_users: number;
}

export interface OrgUsageTimeseries {
  window_days: UsageWindowDays;
  /** Non-empty entries name upstream sources that failed and were zeroed. */
  partial: Array<'spend_logs' | 'spend_logs_capped'>;
  /** Sorted ascending, one entry per day that had activity — sparse, not zero-filled. */
  points: OrgUsageTimeseriesPoint[];
}
